import {
  INTERVAL_SPACING_CONFIG,
  type IntervalSpacingConfig,
} from './keyPoseEditDeviation';

export const DEFAULT_KEY_POSE_INTERVAL_FRAMES = 6;

export type KeyPoseIntervalAugmentationSettings = {
  intervalFrames: number;
  subtleDeviationThreshold: number;
  deviationForMaxSpacing: number;
};

export const DEFAULT_INTERVAL_AUGMENTATION_SETTINGS: KeyPoseIntervalAugmentationSettings =
  {
    intervalFrames: DEFAULT_KEY_POSE_INTERVAL_FRAMES,
    subtleDeviationThreshold: INTERVAL_SPACING_CONFIG.subtleDeviationThreshold,
    deviationForMaxSpacing: INTERVAL_SPACING_CONFIG.deviationForMaxSpacing,
  };

export function toIntervalSpacingConfig(
  settings: KeyPoseIntervalAugmentationSettings,
): IntervalSpacingConfig {
  const subtleDeviationThreshold = Math.max(
    settings.subtleDeviationThreshold,
    0,
  );
  const deviationForMaxSpacing = Math.max(
    settings.deviationForMaxSpacing,
    subtleDeviationThreshold + 0.01,
  );

  return {
    baseSpacingMultiplier: INTERVAL_SPACING_CONFIG.baseSpacingMultiplier,
    maxSpacingMultiplier: INTERVAL_SPACING_CONFIG.maxSpacingMultiplier,
    subtleDeviationThreshold,
    deviationForMaxSpacing,
    rotationWeight: INTERVAL_SPACING_CONFIG.rotationWeight,
  };
}

export function normalizeIntervalAugmentationSettings(
  settings: Partial<KeyPoseIntervalAugmentationSettings>,
  current: KeyPoseIntervalAugmentationSettings = DEFAULT_INTERVAL_AUGMENTATION_SETTINGS,
): KeyPoseIntervalAugmentationSettings {
  const intervalFrames = clampInt(
    resolveNumericSetting(settings.intervalFrames, current.intervalFrames),
    1,
    240,
  );
  const subtleDeviationThreshold = clampFloat(
    resolveNumericSetting(
      settings.subtleDeviationThreshold,
      current.subtleDeviationThreshold,
    ),
    0,
    100,
  );
  const deviationForMaxSpacing = clampFloat(
    resolveNumericSetting(
      settings.deviationForMaxSpacing,
      current.deviationForMaxSpacing,
    ),
    subtleDeviationThreshold + 0.01,
    100,
  );

  return {
    intervalFrames,
    subtleDeviationThreshold,
    deviationForMaxSpacing,
  };
}

function resolveNumericSetting(value: number | undefined, fallback: number) {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

function clampInt(value: number, min: number, max: number) {
  return Math.min(Math.max(Math.round(value), min), max);
}

function clampFloat(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
